
import { defineComponent } from 'vue';

export default defineComponent({
    props: {
        text: { type: String, required: true },

        position: {
            type: String,
            default: 'bottom',
            validator: (v: string) => ['top', 'bottom', 'left', 'right'].includes(v)
        },

        triggerType: {
            type: String,
            default: 'auto',
            validator: (v: string) => ['hover', 'click', 'auto'].includes(v)
        },

        bgColor: { type: String, default: 'bg-white/85' },
        textColor: { type: String, default: 'text-inherit' },
        textSize: { type: String, default: 'text-sm' },
        padding: { type: String, default: 'px-3 py-2' },
        rounded: { type: String, default: 'rounded-none' },
        shadow: { type: String, default: '' },
        outlineClass: { type: String, default: '' },

        backdropBlur: { type: Boolean, default: true },

        arrowColor: { type: String, default: '' }
    },

    data() {
        return {
            showTooltip: false,
            tooltipX: 0,
            tooltipY: 0,
            actualPosition: 'top' as string,
            arrowOffset: 0,
            screenWidth: window.innerWidth
        };
    },

    mounted() {
        window.addEventListener('scroll', this.handleScroll, true);
        window.addEventListener('resize', this.handleResize);
        if (this.effectiveTriggerType === 'click') {
            document.addEventListener('click', this.handleClickOutside);
        }
    },

    unmounted() {
        window.removeEventListener('scroll', this.handleScroll, true);
        window.removeEventListener('resize', this.handleResize);
        if (this.effectiveTriggerType === 'click') {
            document.removeEventListener('click', this.handleClickOutside);
        }
    },

    computed: {
        isMobile(): boolean {
            return this.screenWidth < 1024;
        },

        effectiveTriggerType(): string {
            if (this.triggerType === 'auto') return this.isMobile ? 'click' : 'hover';
            return this.triggerType;
        },

        baseClasses(): string {
            return [
                this.bgColor,
                this.textColor,
                this.textSize,
                this.padding,
                this.rounded,
                this.shadow,
                this.outlineClass
            ]
                .filter(Boolean)
                .join(' ');
        },

        tooltipStyle() {
            let transform = '';
            switch (this.actualPosition) {
                case 'top':
                case 'bottom':
                    transform = 'translateX(-50%)';
                    break;
                case 'left':
                    transform = 'translateX(90%)';
                    break;
                case 'right':
                    transform = 'translateX(0)';
                    break;
            }
            return { left: `${this.tooltipX}px`, top: `${this.tooltipY}px`, transform };
        },

        arrowClasses(): string {
            return 'absolute w-0 h-0 z-10';
        },

        arrowBorderClasses(): string {
            return 'absolute w-0 h-0';
        },

        arrowWrapperStyle() {
            const arrowSize = 6;
            switch (this.actualPosition) {
                case 'top':
                    return {
                        top: '100%',
                        left: `calc(50% + ${this.arrowOffset}px)`,
                        transform: 'translateX(-50%)'
                    };
                case 'bottom':
                    return {
                        bottom: '100%',
                        left: `calc(50% + ${this.arrowOffset}px)`,
                        transform: 'translateX(-50%)'
                    };
                case 'left':
                    return {
                        left: '100%',
                        top: `calc(50% + ${this.arrowOffset}px)`,
                        transform: 'translateY(-50%)'
                    };
                case 'right':
                    return {
                        right: '100%',
                        top: `calc(50% + ${this.arrowOffset}px)`,
                        transform: 'translateY(-50%)'
                    };
                default:
                    return {
                        bottom: '100%',
                        left: `calc(50% + ${this.arrowOffset}px)`,
                        transform: 'translateX(-50%)'
                    };
            }
        },

        arrowComputedColor(): string {
            if (this.arrowColor) return this.arrowColor;
            // naive inference from bgColor utility
            if (this.bgColor.includes('white')) return 'white';
            if (this.bgColor.includes('black')) return 'black';
            if (this.bgColor.includes('gray-')) return 'gray';
            return 'white';
        },

        arrowBorderStyle() {
            const arrowSize = 7;
            const borderColor = 'rgb(156, 163, 175)'; // gray-400

            switch (this.actualPosition) {
                case 'top':
                    return {
                        borderLeft: `${arrowSize}px solid transparent`,
                        borderRight: `${arrowSize}px solid transparent`,
                        borderTop: `${arrowSize}px solid ${borderColor}`
                    };
                case 'bottom':
                    return {
                        borderLeft: `${arrowSize}px solid transparent`,
                        borderRight: `${arrowSize}px solid transparent`,
                        borderBottom: `${arrowSize}px solid ${borderColor}`
                    };
                case 'left':
                    return {
                        borderTop: `${arrowSize}px solid transparent`,
                        borderBottom: `${arrowSize}px solid transparent`,
                        borderLeft: `${arrowSize}px solid ${borderColor}`
                    };
                case 'right':
                    return {
                        borderTop: `${arrowSize}px solid transparent`,
                        borderBottom: `${arrowSize}px solid transparent`,
                        borderRight: `${arrowSize}px solid ${borderColor}`
                    };
                default:
                    return {
                        borderLeft: `${arrowSize}px solid transparent`,
                        borderRight: `${arrowSize}px solid transparent`,
                        borderBottom: `${arrowSize}px solid ${borderColor}`
                    };
            }
        },

        arrowStyle() {
            const arrowSize = 6;
            const color = this.arrowComputedColor;

            switch (this.actualPosition) {
                case 'top':
                    return {
                        top: '0px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        borderLeft: `${arrowSize}px solid transparent`,
                        borderRight: `${arrowSize}px solid transparent`,
                        borderTop: `${arrowSize}px solid ${color}`
                    };
                case 'bottom':
                    return {
                        bottom: '0px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        borderLeft: `${arrowSize}px solid transparent`,
                        borderRight: `${arrowSize}px solid transparent`,
                        borderBottom: `${arrowSize}px solid ${color}`
                    };
                case 'left':
                    return {
                        left: '0px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        borderTop: `${arrowSize}px solid transparent`,
                        borderBottom: `${arrowSize}px solid transparent`,
                        borderLeft: `${arrowSize}px solid ${color}`
                    };
                case 'right':
                    return {
                        right: '0px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        borderTop: `${arrowSize}px solid transparent`,
                        borderBottom: `${arrowSize}px solid transparent`,
                        borderRight: `${arrowSize}px solid ${color}`
                    };
                default:
                    return {
                        bottom: '0px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        borderLeft: `${arrowSize}px solid transparent`,
                        borderRight: `${arrowSize}px solid transparent`,
                        borderBottom: `${arrowSize}px solid ${color}`
                    };
            }
        }
    },

    watch: {
        showTooltip(newVal: boolean) {
            if (newVal) this.$nextTick(() => this.calculateTooltipPosition());
        }
    },

    methods: {
        toggleTooltip(event: Event) {
            if (this.effectiveTriggerType === 'click') {
                event.preventDefault();
                event.stopPropagation();
                this.showTooltip = !this.showTooltip;
            }
        },

        handleClickOutside(event: Event) {
            const target = event.target as HTMLElement;
            const container = this.$refs.container as HTMLElement;
            if (container && !container.contains(target)) this.showTooltip = false;
        },

        handleScroll() {
            if (this.showTooltip) this.showTooltip = false;
        },

        handleResize() {
            this.screenWidth = window.innerWidth;
            if (this.showTooltip) this.showTooltip = false;
        },

        calculateTooltipPosition() {
            const container = this.$refs.container as HTMLElement;
            if (!container) return;

            const rect = container.getBoundingClientRect();
            const tooltipHeight = 37;
            const tooltipWidth = 200;
            const margin = 8;

            const viewport = { width: window.innerWidth, height: window.innerHeight };
            const positionAttempts = this.getPositionPriority(this.position);

            for (const pos of positionAttempts) {
                const result = this.calculatePositionWithArrow(
                    rect,
                    pos,
                    tooltipHeight,
                    tooltipWidth,
                    margin,
                    viewport
                );
                if (result.fitsInViewport) {
                    this.actualPosition = pos;
                    this.tooltipX = result.x;
                    this.tooltipY = result.y;
                    this.arrowOffset = result.arrowOffset;
                    return;
                }
            }

            const result = this.calculatePositionWithArrow(
                rect,
                this.position,
                tooltipHeight,
                tooltipWidth,
                margin,
                viewport,
                true
            );
            this.actualPosition = this.position;
            this.tooltipX = result.x;
            this.tooltipY = result.y;
            this.arrowOffset = result.arrowOffset;
        },

        getPositionPriority(preferred: string): string[] {
            const map: Record<string, string[]> = {
                top: ['top', 'bottom', 'left', 'right'],
                bottom: ['bottom', 'top', 'left', 'right'],
                left: ['left', 'right', 'top', 'bottom'],
                right: ['right', 'left', 'top', 'bottom']
            };
            return map[preferred] || map.top;
        },

        calculatePositionWithArrow(
            rect: DOMRect,
            position: string,
            tooltipHeight: number,
            tooltipWidth: number,
            margin: number,
            viewport: { width: number; height: number },
            forceEdgeFit = false
        ) {
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            let x = 0;
            let y = 0;
            let arrowOffset = 0;

            switch (position) {
                case 'top':
                    x = centerX;
                    y = rect.top - tooltipHeight - margin;
                    break;
                case 'bottom':
                    x = centerX;
                    y = rect.bottom + margin;
                    break;
                case 'left':
                    x = rect.left - tooltipWidth - margin;
                    y = centerY - tooltipHeight / 2;
                    break;
                case 'right':
                    x = rect.right + margin;
                    y = centerY - tooltipHeight / 2;
                    break;
                default:
                    x = centerX;
                    y = rect.bottom + margin;
            }

            if (position === 'top' || position === 'bottom') {
                const originalX = x;
                const halfW = tooltipWidth / 2;
                if (x - halfW < margin) {
                    x = margin + halfW;
                    arrowOffset = originalX - x;
                } else if (x + halfW > viewport.width - margin) {
                    x = viewport.width - margin - halfW;
                    arrowOffset = originalX - x;
                }
                const max = tooltipWidth / 2 - 20;
                arrowOffset = Math.max(-max, Math.min(max, arrowOffset));
            }

            if (position === 'left' || position === 'right') {
                const originalY = y;
                const halfH = tooltipHeight / 2;
                if (y < margin) {
                    y = margin;
                    arrowOffset = originalY + halfH - y - halfH;
                } else if (y + tooltipHeight > viewport.height - margin) {
                    y = viewport.height - margin - tooltipHeight;
                    arrowOffset = originalY + halfH - y - halfH;
                }
                const max = tooltipHeight / 2 - 10;
                arrowOffset = Math.max(-max, Math.min(max, arrowOffset));
            }

            const fitsInViewport =
                forceEdgeFit ||
                (x >= margin &&
                    y >= margin &&
                    x + tooltipWidth <= viewport.width - margin &&
                    y + tooltipHeight <= viewport.height - margin);

            return { x, y, arrowOffset, fitsInViewport };
        }
    }
});
